import React, { useState } from 'react';
import { APP_COLORS as C, APP_FONT as F } from '../tokens';
import { fmtMoney, fmtMoneyShort, fmtMonthLabel } from '../helpers';
import type { CashFlowPoint } from '../types';
import CashFlowTooltip from './CashFlowTooltip';

type Props = {
  points: ReadonlyArray<CashFlowPoint>;
};

export default function CashFlowBars({ points }: Props) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (points.length === 0) {
    return (
      <div style={{ height: 150, display: 'grid', placeItems: 'center', color: C.sub, fontSize: 13, fontStyle: 'italic', fontFamily: F.family }}>
        No money in/out data yet.
      </div>
    );
  }

  const maxVal = Math.max(...points.flatMap((p) => [p.moneyIn, p.moneyOut]), 1);
  const yTicks = 4;
  const stepVal = maxVal / yTicks;

  return (
    <div style={{ fontFamily: F.family }}>
      <div style={{ display: 'flex', height: 180, margin: '10px 0 2px', position: 'relative' }}>
        {/* Y-axis */}
        <div style={{
          width: 44, display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between', fontSize: 10.5, color: C.faint,
          fontWeight: 500, textAlign: 'right', paddingRight: 8, paddingBottom: 2,
        }}>
          {Array.from({ length: yTicks + 1 }, (_, i) => yTicks - i).map((i) => (
            <span key={i}>{fmtMoneyShort(stepVal * i)}</span>
          ))}
        </div>

        {/* Plot area */}
        <div style={{ flex: 1, position: 'relative', borderBottom: '1px solid ' + C.line }}>
          {/* Grid lines */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
            {Array.from({ length: yTicks + 1 }, (_, i) => (
              <div key={i} style={{ height: 1, background: i === yTicks ? C.line : C.lineSoft }} />
            ))}
          </div>

          {/* Bar columns */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', padding: '0 4px' }}>
            {points.map((p, i) => {
              const inPct = (p.moneyIn / maxVal) * 100;
              const outPct = (p.moneyOut / maxVal) * 100;
              const hatchIn = 'repeating-linear-gradient(45deg, #15A08C 0 4px, rgba(21,160,140,0.4) 4px 7px)';
              const hatchOut = 'repeating-linear-gradient(45deg, #B53B2E 0 4px, rgba(181,59,46,0.4) 4px 7px)';
              return (
                <div
                  key={p.monthIso}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  style={{
                    flex: 1, display: 'flex', justifyContent: 'center',
                    alignItems: 'flex-end', gap: 1, height: '100%',
                    position: 'relative', cursor: 'pointer', paddingBottom: 1,
                  }}
                >
                  <div style={{
                    width: 5, height: inPct + '%',
                    background: p.projected ? hatchIn : C.teal,
                    borderRadius: '1px 1px 0 0',
                  }} />
                  <div style={{
                    width: 5, height: outPct + '%',
                    background: p.projected ? hatchOut : C.red,
                    borderRadius: '1px 1px 0 0',
                  }} />
                  {hoverIdx === i && (
                    <CashFlowTooltip
                      title={fmtMoney(p.moneyIn - p.moneyOut)}
                      subtitle={fmtMonthLabel(p.monthIso, true) + (p.projected ? ' (projected)' : ' (actual)')}
                      rows={[
                        { color: C.teal, label: p.projected ? 'Projected in'  : 'Money in',  amount: fmtMoney(p.moneyIn),  opacity: p.projected ? 0.5 : 1 },
                        { color: C.red,  label: p.projected ? 'Projected out' : 'Money out', amount: fmtMoney(p.moneyOut), opacity: p.projected ? 0.5 : 1 },
                      ]}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-around', fontSize: 11.5, color: C.ink, fontWeight: 600, paddingLeft: 44, marginTop: 6 }}>
        {points.map((p) => (
          <span key={p.monthIso}>{fmtMonthLabel(p.monthIso)}</span>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 16, fontSize: 11.5, color: C.ink, marginTop: 14, fontWeight: 500, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: C.teal }} /> Money in
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: C.red }} /> Money out
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 14, height: 10, borderRadius: 2, background: 'repeating-linear-gradient(45deg, #15A08C 0 3px, rgba(21,160,140,0.3) 3px 6px)' }} /> Projected money in
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 14, height: 10, borderRadius: 2, background: 'repeating-linear-gradient(45deg, #B53B2E 0 3px, rgba(181,59,46,0.3) 3px 6px)' }} /> Projected money out
        </span>
      </div>
    </div>
  );
}
